import type { KeyboardEvent } from 'react';

/** The chat composer: attach-a-statement + textarea + send, exactly as it sat
 *  inline on the Assistant page. */
export function AssistantComposer({
  input,
  onInputChange,
  onKeyDown,
  onSend,
  onAttach,
  busy,
  aiPaused,
}: {
  input: string;
  onInputChange: (value: string) => void;
  onKeyDown: (e: KeyboardEvent<HTMLTextAreaElement>) => void;
  onSend: (text: string) => void;
  onAttach: (file: File) => void;
  busy: boolean;
  aiPaused: boolean;
}) {
  return (
    <div className="flex items-end gap-2 rounded-2xl border border-neutral-300 bg-white p-2 shadow-sm focus-within:border-emerald-500 dark:border-neutral-700 dark:bg-neutral-950">
      <label
        className={`inline-flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-xl text-neutral-400 transition hover:bg-neutral-100 hover:text-emerald-600 dark:hover:bg-neutral-800 ${busy || aiPaused ? 'pointer-events-none opacity-40' : ''}`}
        title="Attach a statement — I'll tell you what's in it before anything is ingested"
      >
        <input
          type="file"
          accept=".csv,.xlsx,.xls"
          className="hidden"
          disabled={busy || aiPaused}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onAttach(f);
            e.target.value = '';
          }}
        />
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M21 12.5l-8.5 8.5a6 6 0 01-8.5-8.5l8.5-8.5a4 4 0 015.7 5.7l-8.5 8.5a2 2 0 01-2.8-2.8l7.8-7.8"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </label>
      <textarea
        value={input}
        onChange={(e) => onInputChange(e.target.value)}
        onKeyDown={onKeyDown}
        rows={1}
        disabled={aiPaused}
        placeholder={
          aiPaused ? 'AI assistant is paused' : 'Ask about any member, carrier, or trend…'
        }
        className="max-h-40 flex-1 resize-none bg-transparent px-2 py-1.5 text-sm outline-none disabled:opacity-50"
      />
      <button
        type="button"
        onClick={() => onSend(input)}
        disabled={busy || !input.trim() || aiPaused}
        className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-600 text-white transition hover:bg-emerald-700 disabled:opacity-40"
        aria-label="Send"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M5 12h14M13 6l6 6-6 6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    </div>
  );
}
